import { QuoteOfferingGroups } from '@monetize/types/app';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  apiDelete,
  apiPost,
  apiPut,
  parseMessageFromError,
} from '../../../../../api/axios';
import { cpqServiceQueryKeys } from '../../../../../api/cpqService';
import { logger } from '../../../../../services/logger';
import { ManageGroupsDraftPlan } from './manageQuoteOfferingGroupsUtils';

export type ManageGroupsSaveFailure = {
  /** The group the failed request was for, e.g. "Group 3" */
  label: string;
  message: string;
};

export type ManageGroupsSaveResult = {
  appliedCount: number;
  failures: ManageGroupsSaveFailure[];
};

const toFailure = (label: string, error: unknown): ManageGroupsSaveFailure => {
  logger.error(error);
  const { summary, detail } = parseMessageFromError(error);
  return { label, message: detail || summary || 'Unknown error' };
};

/**
 * Applies a whole staged draft in one go.
 *
 * Requests are sequential and go through the api helpers directly rather than
 * the per-group mutation hooks: those each cancel in-flight queries, patch the
 * groups cache optimistically and invalidate, which would thrash when called in
 * a loop. Here the groups query is invalidated exactly once, after every request
 * has settled.
 *
 * A failed request does not abort the rest — each one is independent, and
 * stopping halfway would leave the user guessing which changes landed. Failures
 * are collected and reported so the caller can re-seed from the server.
 */
export const useApplyQuoteOfferingGroupsDraft = (quoteId: string) => {
  const queryClient = useQueryClient();

  return useMutation<ManageGroupsSaveResult, unknown, ManageGroupsDraftPlan>({
    mutationFn: async ({ deletes, updates, creates }) => {
      const failures: ManageGroupsSaveFailure[] = [];
      let appliedCount = 0;

      for (const { quoteOfferingGroupId, label } of deletes) {
        try {
          await apiDelete<QuoteOfferingGroups>(
            `/api/quoteOfferingGroups/${quoteOfferingGroupId}`,
          );
          appliedCount += 1;
        } catch (error) {
          failures.push(toFailure(label, error));
        }
      }

      for (const { quoteOfferingGroupId, label, payload } of updates) {
        try {
          await apiPut<QuoteOfferingGroups>(
            `/api/quotes/${quoteId}/quoteOfferingGroups/${quoteOfferingGroupId}`,
            payload,
          );
          appliedCount += 1;
        } catch (error) {
          failures.push(toFailure(label, error));
        }
      }

      for (const { label, payload } of creates) {
        try {
          await apiPost<QuoteOfferingGroups>(
            `/api/quotes/${quoteId}/quoteOfferingGroups`,
            payload,
          );
          appliedCount += 1;
        } catch (error) {
          failures.push(toFailure(label, error));
        }
      }

      return { appliedCount, failures };
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [...cpqServiceQueryKeys.quoteOfferingGroups(quoteId)],
      });
    },
  });
};
